import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getImageUrl } from "src/Common/common";
import { can, handleError } from "src/Common/function";
import useTicketMessagesRealtime from "src/hooks/useTicketMessagesRealtime";
import { setTicketNumber } from "src/Redux/Slices/HeaderSlice";
import TicketService from "src/Services/API/ticket.service";
import TicketMessageService from "src/Services/API/ticketmessage.service";
import InfoSection from "./components/InfoSection";
import MarkAsClosedButton from "./components/MarkAsClosedButton";
import ReopenButton from "./components/ReopenButton";
import ChatSection from "./components/ChatSection";
import UserService from "src/Services/API/user.service";

/**
 * TicketDetail — Displays a single ticket's information and chat thread.
 *
 * When this component mounts (user navigates to /ticket/detail/:ticketId):
 *   1. Fetches the full ticket data (including messages) from the backend.
 *   2. Calls markRead to record that the user has seen this ticket, which:
 *      - Upserts a ticket_read_status row with lastReadAt = now() on the backend.
 *      - Emits a WebSocket "read" event so that the sidemenu's red dot indicator
 *        re-evaluates whether any unread tickets remain.
 *
 * Real-time updates via useTicketMessagesRealtime:
 *   - Joins the per-ticket Socket.IO room ("ticket:<ticketId>").
 *   - When a "ticket-message-changed" event arrives (another user sent a message),
 *     automatically re-fetches the ticket data AND calls markRead again, so
 *     incoming messages are immediately marked as read while the user is viewing them.
 *
 * The component also supports:
 *   - Sending new messages (text + attachments) via handleSendNote.
 *   - Closing a ticket (status → 100) via handleCloseTicket.
 *   - Reopening a closed ticket (status → 0) via handleReopenTicket.
 */
const TicketDetail = () => {
  const currentUser = UserService.getCurrentUser();
  const { ticketId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [ticket, setTicket] = useState(location.state?.ticket || null);
  const [isClosing, setIsClosing] = useState(false);
  const [isReopening, setIsReopening] = useState(false);

  /** Fetches the latest ticket data (info + messages) from the backend. */
  const fetchTicket = useCallback(async () => {
    try {
      const response = await TicketService.getOne(ticketId);
      setTicket(response.data);
    } catch (error) {
      handleError(error);
    }
  }, [ticketId]);

  /**
   * On mount: fetch ticket data and mark it as read.
   * markRead is fire-and-forget — errors are silently caught because a failed
   * read-status update should not block the user from viewing the ticket.
   */
  useEffect(() => {
    if (ticketId) {
      fetchTicket();
      TicketService.markRead(ticketId).catch(() => {});
    }
  }, [ticketId, fetchTicket]);

  /**
   * Subscribe to real-time message updates for this specific ticket.
   * When a new message arrives from another user, re-fetch the ticket data
   * (to show the new message in the chat) and call markRead (so the user's
   * read timestamp stays current while they are actively viewing the ticket).
   */
  useTicketMessagesRealtime({
    ticketId,
    onRefresh: () => {
      fetchTicket();
      TicketService.markRead(ticketId).catch(() => {});
    },
  });

  useEffect(() => {
    if (ticket?.number) {
      dispatch(setTicketNumber(String(ticket.number)));
    }
  }, [ticket, dispatch]);

  /**
   * Sends a new message on this ticket, optionally with file attachments.
   * After sending, re-fetches the ticket to update the chat UI.
   */
  const handleSendNote = async (noteText, attachments = []) => {

    if (!ticket?.id) return;

    try {
      const response = await TicketMessageService.createMessage({
        message: noteText,
        ticketId: ticket.id,
        senderUserId: currentUser.id
      });

      const savedMessage = response.data;
      const messageId = savedMessage?.id;

      if (attachments.length && messageId) {
        await TicketMessageService.uploadAttachments(attachments, messageId);
      }
      await fetchTicket();
    } catch (error) {
      handleError(error);
    }
  };

  const handleCloseTicket = async () => {
    if (!ticket?.id || isClosing || ticket?.status === 100) return;
    try {
      setIsClosing(true);
      await TicketService.update({
        id: ticket.id,
        status: 100,
      });
      setTicket((prev) => (prev ? { ...prev, status: 100 } : prev));
      await fetchTicket();
    } catch (error) {
      handleError(error);
    } finally {
      setIsClosing(false);
    }
  };

  const handleReopenTicket = async () => {
    if (!ticket?.id || isReopening || ticket?.status !== 100) return;
    try {
      setIsReopening(true);
      await TicketService.update({
        id: ticket.id,
        status: 0,
      });
      setTicket((prev) => (prev ? { ...prev, status: 0 } : prev));
      await fetchTicket();
    } catch (error) {
      handleError(error);
    } finally {
      setIsReopening(false);
    }
  };

  return (
    <div className="w-full px-5 py-4">
      <div className="w-full h-[56px] flex items-center justify-between">
        <div className="flex items-center cursor-pointer" onClick={() => navigate(-1)}>
          <img src={getImageUrl("/images/common/back_arrow.png")} className="w-[24px]" />
          <span className="font-OpenSansRegular text-[14px] font-[700] text-subTitleColor">back</span>
        </div>
        <div className="flex items-center">
          {/* Closing / reopening a ticket is a staff management action. */}
          {can("ticket:manage") &&
            (ticket?.status === 100 ? (
              <ReopenButton
                isReopening={isReopening}
                onReopen={handleReopenTicket}
                status={ticket?.status}
              />
            ) : (
              <MarkAsClosedButton
                isClosing={isClosing}
                onClose={handleCloseTicket}
                status={ticket?.status}
              />
            ))}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
        <InfoSection ticket={ticket} />

        <div className="flex flex-col gap-4">
          <ChatSection notes={ticket?.messages} onSend={handleSendNote} />

          {/* <div className="border border-borderColor rounded-md">
            <div className="border-b border-borderColor px-4 py-3">
              <span className="font-OpenSansRegular font-[700] text-[14px] text-titleColor">History</span>
            </div>
            <div className="min-h-[160px] px-4 py-4" />
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default TicketDetail;
